import { useContext, useEffect } from 'react';

import { BACKSPLASH_DEFAULT_VALUE, CORNER_RADIUS_SIGN, WATERFALL_DEFAULT_VALUE } from '@common/constants';
import { SideItemOption, SideItemType } from '@common/enums';
import {
    ArrowInfo,
    CornerInfo,
    CornerRadiusModel,
    DrawingEdge,
    DrawingShape,
    RemovedEdgeAndCorners,
    SideItem,
    SideItemInfoModel,
} from '@common/models';
import { useSelectedColor } from '@ee-api/colors-api/useSelectedColor';
import { useCornerRadii } from '@ee-api/corner-radii-api/hooks/useCornerRadii';
import { DrawingShapeContext, useShapeContext } from '@ee-context';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import {
    selectSkipUndoStackUpdates,
    setNeedRedrawCanvas,
    setSkipUndoStackUpdates,
} from '@redux/slices/drawingShapeSlice';
import { selectFabricCanvas } from '@redux/slices/fabricSlice';

import { EditableCornerLabel } from '../drawer/editableLabel';

const useSideItems = () => {
    const canvas = useAppSelector(selectFabricCanvas);
    const dispatch = useAppDispatch();
    const { selectedSideItem, updateSelectedSideItem } = useShapeContext();
    const { drawingShapes, updateDrawingShapes } = useContext(DrawingShapeContext);
    const skipUndoStackUpdates = useAppSelector(selectSkipUndoStackUpdates);

    const { cornerRadii } = useCornerRadii();
    const { selectedValue: selectedColor } = useSelectedColor();

    const createEdgesAndCornersObject = (
        selectedEdge: DrawingEdge,
        shape: DrawingShape,
        sideItem: SideItemInfoModel
    ) => {
        const cornerRadiiInfo = Object.values({ ...shape.cornerMap });
        // EE-968 ensure corners are sorted by filtering each individually (helps with backsplash arrow display)
        const appliedCornersByEdge = [
            ...cornerRadiiInfo.filter((cornerRadiusInfo) => cornerRadiusInfo.id === selectedEdge.startCornerId),
            ...cornerRadiiInfo.filter((cornerRadiusInfo) => cornerRadiusInfo.id === selectedEdge.endCornerId),
        ];

        const arrowInfoBySelectedEdge = shape?.arrowsInfo?.find(
            (arrowInfo) => arrowInfo.drawingEdgeId === selectedEdge.id
        );

        const removedEdgesAndCorners: RemovedEdgeAndCorners = {
            sideItemType: sideItem.sideType,
            drawingEdge: selectedEdge,
            arrowInfo: arrowInfoBySelectedEdge ? arrowInfoBySelectedEdge : null,
            appliedCorners: appliedCornersByEdge,
        };

        return removedEdgesAndCorners;
    };

    const removeEdgeAndCorners = (
        edgesAndCorners: RemovedEdgeAndCorners[],
        currentEdgeAndCorners: RemovedEdgeAndCorners
    ): void => {
        if (!edgesAndCorners.length || !currentEdgeAndCorners) {
            return;
        }

        const foundIndex = edgesAndCorners?.findIndex(
            (removedEdgeAndCorners) =>
                removedEdgeAndCorners.drawingEdge.id === currentEdgeAndCorners.drawingEdge.id &&
                removedEdgeAndCorners.sideItemType !== currentEdgeAndCorners.sideItemType
        );
        if (foundIndex > -1) {
            edgesAndCorners.splice(foundIndex, 1);
        }
    };

    // remove the edge
    const removeElementsBySideItem = (sideItem: SideItemInfoModel) => {
        drawingShapes.map((drawingShape: DrawingShape) => {
            const selectedEdge = drawingShape?.drawingEdges?.find(
                (drawingEdge: DrawingEdge) => drawingEdge?.id === sideItem?.arrowInfo?.drawingEdgeId
            );

            if (selectedEdge) {
                // remove edges and corners on drawingShape structure and keep history of deleted items
                const edgeAndCorners = createEdgesAndCornersObject(selectedEdge, drawingShape, sideItem);

                drawingShape?.removedElementsHistory?.edgesAndCorners.push(edgeAndCorners);

                // reduce the history removed edges and corners array in having one appliance side per each edge
                removeEdgeAndCorners(drawingShape?.removedElementsHistory?.edgesAndCorners || [], edgeAndCorners);

                updateEdgeAndCornersListStructureAfterRemovalAction(selectedEdge, drawingShape);
            }

            return drawingShape;
        });
    };

    const onCornerDimensionEdit = (editedCorner: CornerInfo, shape: DrawingShape) => {
        for (const key of Object.keys(shape.cornerMap)) {
            if (shape.cornerMap[key].id === editedCorner.id) {
                shape.cornerMap[key] = editedCorner;
            }
        }
        updateDrawingShapes([...drawingShapes]);
        dispatch(setNeedRedrawCanvas(true));
        canvas.renderAll();
    };
    const updateEdgeAndCornersListStructureAfterRemovalAction = (selectedEdge: DrawingEdge, shape: DrawingShape) => {
        // update cornerRadii list with selectedEdge info
        const cornerRadiiInfo = Object.values({ ...shape?.cornerMap });
        if (shape?.removedElementsHistory && shape?.removedElementsHistory?.edgesAndCorners?.length === 0) {
            return;
        }
        cornerRadiiInfo.forEach((cornerRadiusInfo) => {
            if (
                cornerRadiusInfo.id === selectedEdge.startCornerId ||
                cornerRadiusInfo.id === selectedEdge.endCornerId
            ) {
                cornerRadiusInfo.isVisibleInCanvas = false;
            }
        });

        // remove drawingEdgeId from arrowInfo  for Area calculation and marked drawingEdge as not visible in canvas
        const arrowInfoWithEdge = shape?.arrowsInfo?.find((arrowInfo) => arrowInfo?.drawingEdgeId === selectedEdge?.id);

        if (!arrowInfoWithEdge) {
            return;
        }

        arrowInfoWithEdge.isActiveForEdgeCalc = false;
        selectedEdge.isVisibleInCanvas = false;

        // remove side item information from DrawingShape object in case a side item (backsplash/waterfall) has been selected previously
        if (selectedEdge.sideItem.depth > 0) {
            selectedEdge.sideItem = new SideItem();
            arrowInfoWithEdge.isVisibleInCanvas = true;
        }
    };

    const updateEdgeAndCornersListStructureAfterUndoneAction = (
        shape: DrawingShape,
        removedEdgeAndCornerInfo: RemovedEdgeAndCorners,
        cornerRadii: CornerRadiusModel[]
        // eslint-disable-next-line sonarjs/cognitive-complexity
    ) => {
        // reset the is visible flag from drawingEdge and ArrowInfo
        shape?.arrowsInfo?.forEach((arrowInfo) => {
            if (arrowInfo?.drawingEdgeId === removedEdgeAndCornerInfo?.arrowInfo?.drawingEdgeId) {
                arrowInfo.isActiveForEdgeCalc = true;
                arrowInfo.isVisibleInCanvas = true;
            }
        });

        shape?.drawingEdges?.forEach((drawingEdge) => {
            if (drawingEdge.id === removedEdgeAndCornerInfo.drawingEdge?.id) {
                drawingEdge.isVisibleInCanvas = true;

                // if there is side item information when uncheck the side item, reset it
                if (drawingEdge?.sideItem?.depth > 0) {
                    drawingEdge.sideItem = new SideItem();
                }
            }
        });

        // make cornerRadii applicable again by setting isVisibleInCanvas flag to true
        const cornerRadiiInfo = Object.values(shape.cornerMap);

        cornerRadiiInfo.forEach((cornerInfo, index) => {
            const appliedCorner = removedEdgeAndCornerInfo.appliedCorners.find((el) => el.id === cornerInfo.id);
            if (
                appliedCorner &&
                shape.drawingEdges.find((el) => el.startCornerId === appliedCorner.id)?.isVisibleInCanvas &&
                shape.drawingEdges.find((el) => el.endCornerId === appliedCorner.id)?.isVisibleInCanvas
            ) {
                cornerInfo.isVisibleInCanvas = true;
                //drawingTool.addCornerRadiusMeasureText(canvas, shape, index, cornerInfo);

                shape.editableLabels.push(
                    new EditableCornerLabel(
                        shape,
                        cornerInfo,
                        CORNER_RADIUS_SIGN,
                        onCornerDimensionEdit,
                        canvas,
                        selectedColor?.overlayColor,
                        index,
                        cornerRadii
                    )
                );
            } else if (appliedCorner) {
                cornerInfo.isVisibleInCanvas = false;
            }
        });

        // remove undone Edges and Corners info from removedElementsHistory object
        if (shape.removedElementsHistory) {
            const removedEdgesAndCornersIndex = shape.removedElementsHistory.edgesAndCorners.findIndex(
                (edgeAndCorners: RemovedEdgeAndCorners) =>
                    edgeAndCorners.arrowInfo?.drawingEdgeId === selectedSideItem?.arrowInfo?.drawingEdgeId
            );

            if (removedEdgesAndCornersIndex > -1) {
                shape.removedElementsHistory?.edgesAndCorners.splice(removedEdgesAndCornersIndex, 1);
            }
        }

        // reset waterfall and backsplash values after unchecking any of them
        if (selectedSideItem) {
            if (selectedSideItem.backsplash) {
                const defaultSelectedSideItem = {
                    ...selectedSideItem,
                    backsplash: { checked: false, height: BACKSPLASH_DEFAULT_VALUE },
                };
                updateSelectedSideItem(defaultSelectedSideItem);
            }

            if (selectedSideItem.waterfallHeight) {
                const defaultSelectedSideItem = { ...selectedSideItem, waterfallHeight: WATERFALL_DEFAULT_VALUE };
                updateSelectedSideItem(defaultSelectedSideItem);
            }
        }
    };

    const undoRemovedElementsBySideItem = (sideItem: SideItemInfoModel, cornerRadii: CornerRadiusModel[]) => {
        const dShapes = drawingShapes.map((drawingShape: DrawingShape) => {
            // undo changes on DrawingShape structure
            const removedEdgeAndCornerInfo = drawingShape?.removedElementsHistory?.edgesAndCorners.find(
                (edgeAndCorners: RemovedEdgeAndCorners) =>
                    edgeAndCorners.arrowInfo?.drawingEdgeId === sideItem?.arrowInfo?.drawingEdgeId
            );

            if (removedEdgeAndCornerInfo) {
                updateEdgeAndCornersListStructureAfterUndoneAction(drawingShape, removedEdgeAndCornerInfo, cornerRadii);
            }

            return drawingShape;
        });

        updateDrawingShapes([...dShapes]);
        dispatch(setNeedRedrawCanvas(true));
    };

    const findEdgeAndArrowBySelectedSideItem = (
        shape: DrawingShape
    ): { foundDrawingEdge: DrawingEdge | undefined; foundArrowInfo: ArrowInfo | undefined } => {
        const foundDrawingEdge = shape?.drawingEdges.find(
            (drawingEdge) => drawingEdge?.id === selectedSideItem?.arrowInfo?.drawingEdgeId
        );

        const foundArrowInfo = shape?.arrowsInfo?.find(
            (arrowInfo) => arrowInfo?.drawingEdgeId === selectedSideItem?.arrowInfo?.drawingEdgeId
        );

        return {
            foundDrawingEdge,
            foundArrowInfo,
        };
    };

    const addRectangleSideItem = () => {
        dispatch(setNeedRedrawCanvas(false));

        updateDrawingShapes((prevState) => {
            return prevState.map((shape: DrawingShape) => {
                const { foundDrawingEdge, foundArrowInfo } = findEdgeAndArrowBySelectedSideItem(shape);

                if (foundDrawingEdge && foundArrowInfo) {
                    foundArrowInfo.isVisibleInCanvas = false;
                    if (selectedSideItem?.waterfallHeight) {
                        foundDrawingEdge.sideItem.depth = selectedSideItem.waterfallHeight;
                        foundDrawingEdge.sideItem.type = SideItemOption.WATERFALL;
                    }

                    if (selectedSideItem?.backsplash?.height) {
                        foundDrawingEdge.sideItem.depth = selectedSideItem.backsplash.height;
                        foundDrawingEdge.sideItem.type = SideItemOption.BACKSPLASH;
                    }
                }

                return shape;
            });
        });

        dispatch(setNeedRedrawCanvas(true));
    };

    const removeDrawnSideItem = () => {
        const dShapes = drawingShapes.map((shape: DrawingShape) => {
            const { foundDrawingEdge, foundArrowInfo } = findEdgeAndArrowBySelectedSideItem(shape);
            if (foundDrawingEdge && foundArrowInfo) {
                // remove the drawn side item information from DrawingShape object
                foundDrawingEdge.sideItem.depth = 0;
                if (foundDrawingEdge.sideItem.arrowsInfo) {
                    // explicitly remove the array entries, otherwise they are still used from other references
                    foundDrawingEdge.sideItem.arrowsInfo.splice(0, foundDrawingEdge.sideItem.arrowsInfo.length);
                }

                foundArrowInfo.isVisibleInCanvas = true;
            }

            return shape;
        });
        updateDrawingShapes([...dShapes]);
        dispatch(setNeedRedrawCanvas(true));
    };

    // on side type select
    useEffect(() => {
        if (!selectedSideItem || skipUndoStackUpdates) {
            return;
        }
        if (selectedSideItem.checked) {
            switch (selectedSideItem.sideType) {
                case SideItemType.WALL_SIDE:
                    if (selectedSideItem.backsplash?.checked) {
                        addRectangleSideItem();
                    } else {
                        removeDrawnSideItem();
                    }
                    break;
                case SideItemType.WATERFALL:
                    addRectangleSideItem();
                    break;
                default:
                    updateDrawingShapes([...drawingShapes]);
                    dispatch(setNeedRedrawCanvas(true));
                    break;
            }
        } else {
            dispatch(setSkipUndoStackUpdates(true));
            undoRemovedElementsBySideItem(selectedSideItem, cornerRadii);
        }
    }, [
        // TODO Evaluate this incomplete dependency list
        selectedSideItem,
        selectedSideItem?.checked,
        selectedSideItem?.backsplash?.checked,
        selectedSideItem?.backsplash?.height,
        selectedSideItem?.waterfallHeight,
    ]);

    return {
        removeElementsBySideItem,
    };
};

export default useSideItems;
